/**
 * Werte des Inline-Editors.
 *
 * Der Editor setzt einen Wertesatz auf alle gewaehlten Zeitraeume desselben Apartments. Die
 * Abbildung auf den Serververtrag laeuft ueber dieselben Bausteine wie der Apartment-Tab
 * (`toBackendRange`); fachliche Gueltigkeit entscheidet weiterhin der Server.
 */
import type { AriPushBatchRange } from '@/shared/api';
import {
  rowHasAnyValue,
  toBackendRange,
  type AvailabilityMode,
  type MultiRangeRow,
  type RestrictionMode,
} from '@/features/properties/ari-edit';
import type { SelectedRange } from './rangeSelectionModel';

export interface InlineEditorValues {
  rate: string;
  availability: AvailabilityMode;
  minStay: string;
  maxStay: string;
  closedToArrival: RestrictionMode;
  closedToDeparture: RestrictionMode;
  stopSell: RestrictionMode;
}

export const EMPTY_EDITOR_VALUES: InlineEditorValues = {
  rate: '',
  availability: '',
  minStay: '',
  maxStay: '',
  closedToArrival: '',
  closedToDeparture: '',
  stopSell: '',
};

export type RestrictionKey = 'closedToArrival' | 'closedToDeparture' | 'stopSell';

export function toRow(range: SelectedRange, values: InlineEditorValues): MultiRangeRow {
  return { id: range.id, from: range.from, to: range.to, status: 'idle', ...values };
}

export function toBatchRanges(
  ranges: readonly SelectedRange[],
  values: InlineEditorValues,
): AriPushBatchRange[] {
  return ranges.map((range) => toBackendRange(toRow(range, values)));
}

export function hasAnyValue(values: InlineEditorValues): boolean {
  return rowHasAnyValue(toRow({ id: 'probe', from: '', to: '' }, values));
}

export function hasAdvancedValue(values: InlineEditorValues): boolean {
  return (
    values.maxStay.trim() !== '' ||
    values.closedToArrival !== '' ||
    values.closedToDeparture !== '' ||
    values.stopSell !== ''
  );
}

/** Was der Editor mit den gewählten Zeiträumen tut, als Satzende („werden blockiert“). */
export function effectLabel(availability: InlineEditorValues['availability']): string {
  if (availability === 'blocked') return 'werden blockiert';
  if (availability === 'available') return 'werden freigegeben';
  return 'werden geändert';
}
